import React, { useMemo, useState, useEffect } from "react";
import {
    Box,
    Typography,
    Grid,
    Paper,
    Stack,
    Chip,
    FormControl,
    Select,
    MenuItem,
    IconButton,
    Divider,
    Avatar,
    Button,
    useTheme,
    useMediaQuery,
    ToggleButtonGroup,
    ToggleButton,
    Dialog,
    TextField,
    DialogContent,
    DialogActions,
    DialogTitle,
    Tabs,
    Tab,
    Tooltip,
} from "@mui/material";

import { subscribeTeachers } from "../services/teachers.service";
import {
    subscribeTeacherAvailability,
    setBlockedDate,
    setBlockedWeekly,
    setOpenOverride,
    saveBooking as saveBookingFB,
    clearBooking as clearBookingFB,
} from "../services/availability.service";

import MoreVertIcon from "@mui/icons-material/MoreVert";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";

import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import TodayRoundedIcon from "@mui/icons-material/TodayRounded";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { classifyBooking, isNegotiationLock, isLockExpired, lockRemainingLabel } from "../utils/bookingKind";

/* =======================
   Helpers
======================= */
function initials(name = "") {
    return name
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((p) => p[0]?.toUpperCase())
        .join("");
}

function getWeekRangeMonToSun(baseDate) {
    const d = new Date(baseDate);
    d.setHours(0, 0, 0, 0);

    const day = d.getDay(); // 0 dom ... 6 sáb
    const diffToMonday = (day + 6) % 7;

    const start = new Date(d);
    start.setDate(d.getDate() - diffToMonday);
    start.setHours(0, 0, 0, 0);

    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    end.setHours(23, 59, 59, 999);

    return { start, end };
}

function addDays(date, days) {
    const d = new Date(date);
    d.setDate(d.getDate() + days);
    return d;
}

function formatWeekLabelMonToSun(weekStart) {
    const s = dayjs(weekStart);
    const e = s.add(6, "day");
    return `${s.format("DD/MM")} — ${e.format("DD/MM")}`;
}

function fmtDayShort(date) {
    const days = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
    const d = new Date(date);
    return days[d.getDay()];
}

function fmtDayLabel(date) {
    const d = new Date(date);
    return `${fmtDayShort(d)} ${String(d.getDate()).padStart(2, "0")}/${String(
        d.getMonth() + 1
    ).padStart(2, "0")}`;
}

function dayNamePT(dow) {
    const map = ["Domingo", "Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado"];
    return map[dow] || "Dia";
}

/* =======================
   Mini KPI
======================= */
function MiniMetric({ title, value, icon }) {
    return (
        <Paper
            elevation={0}
            sx={{
                p: 2,
                borderRadius: 2,
                border: "1px solid var(--border)",
                bgcolor: "rgba(255,255,255,0.55)",
                backdropFilter: "blur(10px)",
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                width: "100%",
            }}
        >
            <Box
                sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 2,
                    display: "grid",
                    placeItems: "center",
                    bgcolor: "rgba(33,49,89,0.10)",
                    color: "var(--cise-blue)",
                    flex: "0 0 auto",
                }}
            >
                {icon}
            </Box>

            <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 900, color: "var(--cise-text-muted)" }}>
                    {title}
                </Typography>
                <Typography sx={{ fontSize: 22, fontWeight: 950 }}>{value}</Typography>
            </Box>
        </Paper>
    );
}

/* =======================
   Mode Switch
======================= */
function ModeSwitch({ value, onChange }) {
    return (
        <ToggleButtonGroup
            exclusive
            value={value}
            onChange={(_, v) => v && onChange(v)}
            size="small"
            sx={{
                bgcolor: "rgba(255,255,255,0.70)",
                borderRadius: 999,
                border: "1px solid var(--border)",
                overflow: "hidden",
                backdropFilter: "blur(10px)",
                "& .MuiToggleButton-root": {
                    textTransform: "none",
                    fontWeight: 950,
                    px: 1.6,
                    py: 0.75,
                    border: "none",
                    gap: 0.8,
                },
            }}
        >
            <ToggleButton value="AGENDAMENTOS">
                <CalendarMonthRoundedIcon sx={{ fontSize: 18 }} />
                Agendamentos
            </ToggleButton>
            <ToggleButton value="DISPONIBILIDADE">
                <GroupsRoundedIcon sx={{ fontSize: 18 }} />
                Disponibilidade
            </ToggleButton>
        </ToggleButtonGroup>
    );
}


function buildSlots(start = "08:00", count = 28, stepMinutes = 30) {
    const [h0, m0] = start.split(":").map(Number);
    const out = [];
    let minutes = h0 * 60 + m0;

    for (let i = 0; i < count; i++) {
        const hh = String(Math.floor(minutes / 60)).padStart(2, "0");
        const mm = String(minutes % 60).padStart(2, "0");
        out.push(`${hh}:${mm}`);
        minutes += stepMinutes;
    }

    return out;
}

const timeSlots = buildSlots("08:00", 24, 30); // 08:00..19:30

function timeToMinutes(hhmm) {
    const [h, m] = String(hhmm).split(":").map(Number);
    return h * 60 + m;
}

const BUSINESS_HOURS = {
    1: { start: "09:00", end: "19:30" }, // Seg
    2: { start: "09:00", end: "19:30" }, // Ter
    3: { start: "09:00", end: "19:30" }, // Qua
    4: { start: "09:00", end: "19:30" }, // Qui
    5: { start: "09:00", end: "18:30" }, // Sex
    6: { start: "08:00", end: "12:00" }, // Sáb
    0: null, // Dom fechado
};

function isOutsideBusinessHours(dayDate, slot) {
    const d = new Date(dayDate);
    const dow = d.getDay(); // 0 dom .. 6 sab
    const rule = BUSINESS_HOURS[dow];
    if (!rule) return true; // domingo fechado

    const t = timeToMinutes(slot);
    const start = timeToMinutes(rule.start);
    const end = timeToMinutes(rule.end);

    // slot fora do intervalo [start, end] => bloqueado
    return t < start || t > end;
}

/* =======================
   Booking Dialog
======================= */
function BookingDialog({
    open,
    onClose,
    onSave,
    onClear,
    teacherName,
    teacherId,
    dayDate,
    slotTime,
    initialData,
}) {
    const [form, setForm] = useState({
        nome: "",
        escola: "",
        bairro: "",
        idade: "",
        conhecimento: "",
        professorId: teacherId || "",
        compareceu: "",
        matriculado: "",
        color: "#213159",
    });

    useEffect(() => {
        if (!open) return;

        setForm({
            nome: initialData?.nome || "",
            escola: initialData?.escola || "",
            bairro: initialData?.bairro || "",
            idade: initialData?.idade || "",
            conhecimento: initialData?.conhecimento || "",
            professorId: teacherId || initialData?.professorId || "",
            compareceu: initialData?.compareceu || "",
            matriculado: initialData?.matriculado || "",
            color: initialData?.color || "#213159",
        });
    }, [open, initialData, teacherId]);

    const dayLabel = dayDate ? fmtDayLabel(dayDate) : "";
    const canSave = !!form.nome?.trim();

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="sm"
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    border: "1px solid var(--border)",
                    bgcolor: "rgba(255,255,255,0.92)",
                    backdropFilter: "blur(14px)",
                },
            }}
        >
            <DialogTitle sx={{ fontWeight: 950 }}>
                Cadastro • {dayLabel} • {slotTime} •{" "}
                <span style={{ color: "var(--cise-blue)" }}>{teacherName}</span>
            </DialogTitle>

            <DialogContent dividers sx={{ borderColor: "var(--border)" }}>
                <Stack spacing={1.4} sx={{ pt: 1 }}>
                    <TextField
                        label="Nome"
                        value={form.nome}
                        onChange={(e) => setForm((p) => ({ ...p, nome: e.target.value }))}
                        fullWidth
                    />

                    <TextField
                        label="Escola"
                        value={form.escola}
                        onChange={(e) => setForm((p) => ({ ...p, escola: e.target.value }))}
                        fullWidth
                    />

                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2}>
                        <TextField
                            label="Bairro"
                            value={form.bairro}
                            onChange={(e) => setForm((p) => ({ ...p, bairro: e.target.value }))}
                            fullWidth
                        />
                        <TextField
                            label="Idade"
                            value={form.idade}
                            onChange={(e) => setForm((p) => ({ ...p, idade: e.target.value }))}
                            fullWidth
                        />
                    </Stack>

                    <TextField
                        label="Conhecimento"
                        value={form.conhecimento}
                        onChange={(e) => setForm((p) => ({ ...p, conhecimento: e.target.value }))}
                        fullWidth
                    />

                    <TextField label="Professor" value={teacherName} fullWidth disabled />

                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2}>
                        <FormControl fullWidth>
                            <Typography sx={{ fontSize: 12, fontWeight: 900, color: "var(--cise-text-muted)", mb: 0.5 }}>
                                Compareceu?
                            </Typography>
                            <Select
                                value={form.compareceu}
                                onChange={(e) => setForm((p) => ({ ...p, compareceu: e.target.value }))}
                                size="small"
                            >
                                <MenuItem value="">—</MenuItem>
                                <MenuItem value="Sim">Sim</MenuItem>
                                <MenuItem value="Não">Não</MenuItem>
                            </Select>
                        </FormControl>

                        <FormControl fullWidth>
                            <Typography sx={{ fontSize: 12, fontWeight: 900, color: "var(--cise-text-muted)", mb: 0.5 }}>
                                Matriculado?
                            </Typography>
                            <Select
                                value={form.matriculado}
                                onChange={(e) => setForm((p) => ({ ...p, matriculado: e.target.value }))}
                                size="small"
                            >
                                <MenuItem value="">—</MenuItem>
                                <MenuItem value="Sim">Sim</MenuItem>
                                <MenuItem value="Não">Não</MenuItem>
                            </Select>
                        </FormControl>
                    </Stack>

                    <TextField
                        label="Cor do bloco (hex)"
                        value={form.color}
                        onChange={(e) => setForm((p) => ({ ...p, color: e.target.value }))}
                        fullWidth
                        helperText="Ex: #213159 (opcional)"
                    />
                </Stack>
            </DialogContent>

            <DialogActions sx={{ p: 2, gap: 1 }}>
                <Button
                    onClick={onClose}
                    variant="outlined"
                    sx={{ borderRadius: 999, fontWeight: 950, textTransform: "none", border: "1px solid var(--border)" }}
                >
                    Cancelar
                </Button>

                <Button
                    onClick={onClear}
                    variant="outlined"
                    color="error"
                    sx={{ borderRadius: 999, fontWeight: 950, textTransform: "none" }}
                >
                    Limpar horário
                </Button>

                <Button
                    onClick={() => onSave(form)}
                    disabled={!canSave}
                    variant="contained"
                    sx={{ borderRadius: 999, fontWeight: 950, textTransform: "none", bgcolor: "var(--cise-blue)" }}
                >
                    Salvar
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function BlockChoiceDialog({
    open,
    onClose,
    ctx,
    onBlockDate,
    onBlockWeekly,
    onUnblockDate,
    onUnblockWeekly,
    onOpenOnlyThisDay,
    onRemoveOpenOnlyThisDay,
}) {
    if (!ctx) return null;

    const { dow, slot, hasDateBlock, hasWeeklyBlock, hasOpenOverride } = ctx;
    const dayLabel = dayNamePT(dow);

    // "Livre" real = não tem date, não tem weekly, e não tem override
    // (override só faz sentido quando existe weekly, mas deixa assim)
    const isBlocking = !hasDateBlock && !hasWeeklyBlock;

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
            <DialogTitle sx={{ fontWeight: 950 }}>
                {isBlocking ? "Bloquear horário" : "Gerenciar bloqueio"}
            </DialogTitle>

            <DialogContent dividers sx={{ borderColor: "var(--border)" }}>
                <Typography sx={{ fontWeight: 900, mb: 1 }}>
                    {dayLabel} • {slot}
                </Typography>

                {isBlocking ? (
                    <Stack spacing={1}>
                        <Button
                            variant="contained"
                            onClick={onBlockDate}
                            sx={{
                                fontWeight: 950,
                                textTransform: "none",
                                borderRadius: 2,
                                bgcolor: "var(--cise-blue)",
                            }}
                        >
                            Bloquear só este dia (pontual)
                        </Button>

                        <Button
                            variant="outlined"
                            onClick={onBlockWeekly}
                            sx={{
                                fontWeight: 950,
                                textTransform: "none",
                                borderRadius: 2,
                                border: "1px solid var(--border)",
                            }}
                        >
                            Bloquear todas as {dayLabel.toLowerCase()}s (recorrente)
                        </Button>
                    </Stack>
                ) : (
                    <Stack spacing={1}>
                        {/* 1) Se tem bloqueio pontual, pode remover pontual */}
                        {hasDateBlock && (
                            <Button
                                variant="contained"
                                color="error"
                                onClick={onUnblockDate}
                                sx={{ fontWeight: 950, textTransform: "none", borderRadius: 2 }}
                            >
                                Remover bloqueio só deste dia
                            </Button>
                        )}

                        {/* 2) Se tem bloqueio recorrente, pode remover recorrente */}
                        {hasWeeklyBlock && (
                            <Button
                                variant="outlined"
                                color="error"
                                onClick={onUnblockWeekly}
                                sx={{ fontWeight: 950, textTransform: "none", borderRadius: 2 }}
                            >
                                Remover bloqueio recorrente (todas as {dayLabel.toLowerCase()}s)
                            </Button>
                        )}

                        {/* 3) EXCEÇÃO: Se tem bloqueio recorrente, permitir liberar só este dia */}
                        {hasWeeklyBlock && !hasOpenOverride && (
                            <Button
                                variant="contained"
                                onClick={onOpenOnlyThisDay}
                                sx={{
                                    fontWeight: 950,
                                    textTransform: "none",
                                    borderRadius: 2,
                                    bgcolor: "rgba(22,163,74,0.95)",
                                    "&:hover": { bgcolor: "rgba(22,163,74,0.95)" },
                                }}
                            >
                                Liberar só este dia (exceção)
                            </Button>
                        )}

                        {hasWeeklyBlock && hasOpenOverride && (
                            <Button
                                variant="outlined"
                                onClick={onRemoveOpenOnlyThisDay}
                                sx={{
                                    fontWeight: 950,
                                    textTransform: "none",
                                    borderRadius: 2,
                                    border: "1px solid var(--border)",
                                }}
                            >
                                Remover liberação deste dia
                            </Button>
                        )}

                        <Typography sx={{ mt: 1, fontSize: 12, color: "var(--cise-text-muted)" }}>
                            Dica: “Liberar só este dia” mantém o bloqueio recorrente ativo para os outros dias.
                        </Typography>
                    </Stack>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 2 }}>
                <Button onClick={onClose} sx={{ fontWeight: 950, textTransform: "none" }}>
                    Fechar
                </Button>
            </DialogActions>
        </Dialog>
    );
}

/* =======================
   Google-like Grid (shared)
   - mode:
       DISPONIBILIDADE -> mostra Livre/Bloqueado/Booking
       AGENDAMENTOS    -> mostra só Booking (livre/bloq vazio)
======================= */
function TeacherWeekGrid({
    weekDays,
    timeSlots,
    availability,
    teacherId,
    mode,
    onCellClick,
    height = 720,
}) {
    const TIME_COL_W = 86;
    const HEADER_H = 54;
    const ROW_H = 44;

    return (
        <Box
            sx={{
                height,
                borderTop: "1px solid var(--border)",
                position: "relative",
                overflow: "auto",
                bgcolor: "rgba(255,255,255,0.35)",
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    position: "sticky",
                    top: 0,
                    zIndex: 20,
                    backdropFilter: "blur(12px)",
                    bgcolor: "rgba(255,255,255,0.75)",
                    borderBottom: "1px solid var(--border)",
                }}
            >
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: `${TIME_COL_W}px repeat(${weekDays.length}, minmax(190px, 1fr))`,
                        height: HEADER_H,
                        alignItems: "center",
                    }}
                >
                    <Box
                        sx={{
                            height: "100%",
                            borderRight: "1px solid var(--border)",
                            position: "sticky",
                            left: 0,
                            zIndex: 25,
                            bgcolor: "rgba(255,255,255,0.90)",
                        }}
                    />
                    {weekDays.map((d) => (
                        <Box
                            key={dayjs(d).format("YYYY-MM-DD")}
                            sx={{
                                px: 1.2,
                                height: "100%",
                                display: "flex",
                                alignItems: "center",
                                borderRight: "1px solid var(--border)",
                            }}
                        >
                            <Typography sx={{ fontWeight: 950, color: "var(--cise-blue)", fontSize: 12 }}>
                                {fmtDayLabel(d)}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            </Box>

            {/* Body */}
            <Box sx={{ minWidth: TIME_COL_W + weekDays.length * 190 }}>
                {timeSlots.map((slot) => (
                    <Box
                        key={slot}
                        sx={{
                            display: "grid",
                            gridTemplateColumns: `${TIME_COL_W}px repeat(${weekDays.length}, minmax(190px, 1fr))`,
                            height: ROW_H,
                        }}
                    >
                        {/* Time col sticky */}
                        <Box
                            sx={{
                                position: "sticky",
                                left: 0,
                                zIndex: 10,
                                borderRight: "1px solid var(--border)",
                                borderBottom: "1px solid var(--border)",
                                bgcolor: "rgba(255,255,255,0.72)",
                                backdropFilter: "blur(10px)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <Typography sx={{ fontSize: 12, fontWeight: 950, color: "var(--cise-text-muted)" }}>
                                {slot}
                            </Typography>
                        </Box>

                        {weekDays.map((d) => {
                            const dayKey = dayjs(d).format("YYYY-MM-DD");
                            const cell = availability?.[teacherId]?.[dayKey]?.[slot] ?? null;

                            const isFree = cell === null;
                            const isBlocked = cell?.type === "blocked";
                            const isNegotiation = cell?.type === "negotiation";
                            const isBooking = cell?.type === "booking";
                            const isTaken = isBooking || isNegotiation;

                            const showFreeLabels = mode === "DISPONIBILIDADE" || mode === "AGENDAMENTOS"; // ✅ agora mostra em ambos
                            const shouldRenderBlock = isTaken || (showFreeLabels && (isFree || isBlocked));

                            const label = isNegotiation
                                ? "Em negociação"
                                : isBooking
                                    ? cell.nome
                                    : isBlocked ? "Bloqueado" : "Livre";

                            // A reserva em negociação é âmbar: ocupa o horário, mas ainda
                            // não é um agendamento — pode voltar a ficar livre a qualquer momento.
                            const bg =
                                isNegotiation
                                    ? "rgba(217,119,6,0.14)"
                                    : isBooking
                                        ? "rgba(33,49,89,0.10)"
                                        : isFree
                                            ? "rgba(22,163,74,0.12)"      // verdinho
                                            : "rgba(220,38,38,0.12)";     // avermelhado

                            const color =
                                isNegotiation
                                    ? "rgba(180,83,9,0.95)"
                                    : isBooking
                                        ? "var(--cise-blue)"
                                        : isFree
                                            ? "rgba(22,163,74,0.95)"
                                            : "rgba(220,38,38,0.95)";

                            return (
                                <Box
                                    key={`${dayKey}-${slot}`}
                                    onClick={() => onCellClick(d, slot)}
                                    role="button"
                                    tabIndex={0}
                                    sx={{
                                        borderRight: "1px solid var(--border)",
                                        borderBottom: "1px solid var(--border)",
                                        cursor: "pointer",
                                        px: 0.8,
                                        py: 0.6,
                                        transition: "transform 120ms ease, background 120ms ease",
                                        "&:hover": { transform: "translateY(-1px)" },
                                        bgcolor: "rgba(255,255,255,0.18)",
                                    }}
                                >
                                    {shouldRenderBlock ? (
                                        <Box
                                            sx={{
                                                height: "100%",
                                                borderRadius: 1.4,
                                                border: "1px solid var(--border)",
                                                bgcolor: bg,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                fontWeight: 950,
                                                fontSize: 12,
                                                color,
                                                userSelect: "none",
                                                px: 1,
                                                overflow: "hidden",
                                                whiteSpace: "nowrap",
                                                textOverflow: "ellipsis",
                                            }}
                                        >
                                            {label}
                                        </Box>
                                    ) : (
                                        <Box sx={{ height: "100%" }} />
                                    )}
                                </Box>
                            );
                        })}
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

/* =======================
   Page
======================= */
export default function AgendaPage() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    const [mode, setMode] = useState("AGENDAMENTOS");

    const [blockDialogOpen, setBlockDialogOpen] = useState(false);
    const [blockCtx, setBlockCtx] = useState(null);

    // Semana compartilhada
    const [weekStart, setWeekStart] = useState(() => getWeekRangeMonToSun(new Date()).start);
    const weekDays = useMemo(() => Array.from({ length: 6 }).map((_, i) => addDays(weekStart, i)), [weekStart]); // SEG..SÁB
    const [mobileDayIdx, setMobileDayIdx] = useState(0);
    const weekLabel = formatWeekLabelMonToSun(weekStart);

    const [teachers, setTeachers] = useState([]);
    const [selectedTeacherId, setSelectedTeacherId] = useState(null);

    // snapshots firebase
    const [fbBlockedDates, setFbBlockedDates] = useState({});
    const [fbBlockedWeekly, setFbBlockedWeekly] = useState({});
    const [fbBookings, setFbBookings] = useState({});

    // Dialog booking
    const [bookingOpen, setBookingOpen] = useState(false);
    const [bookingCtx, setBookingCtx] = useState(null); // { dayDate, slot, teacherId, existing }


    const [fbOpenOverrides, setFbOpenOverrides] = useState({});


    useEffect(() => {
        const unsub = subscribeTeachers((list) => {
            // opcional: só ativos
            const active = list.filter((t) => t.active !== false);
            setTeachers(active);

            // se não tiver selecionado, seleciona o primeiro
            if (!selectedTeacherId && active[0]?.id) setSelectedTeacherId(active[0].id);
        });

        return () => unsub?.();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!selectedTeacherId) return;

        const unsub = subscribeTeacherAvailability(selectedTeacherId, ({ blockedDates, blockedWeekly, openOverrides, bookings }) => {
            setFbBlockedDates(blockedDates || {});
            setFbBlockedWeekly(blockedWeekly || {});
            setFbOpenOverrides(openOverrides || {});
            setFbBookings(bookings || {});
        });

        return () => unsub?.();
    }, [selectedTeacherId]);




    const currentTeacherName =
        teachers.find((t) => t.id === selectedTeacherId)?.name || "Professor";

    function setWeekByDate(jsDate) {
        const base = jsDate instanceof Date ? jsDate : new Date(jsDate);
        if (Number.isNaN(base.getTime())) return;

        const { start } = getWeekRangeMonToSun(base);
        setWeekStart(start);
        setMobileDayIdx(0);
    }

    function handleWeekPick(value) {
        const jsDate = value?.toDate?.() ? value.toDate() : value;
        setWeekByDate(jsDate);
    }

    function shiftWeek(deltaWeeks) {
        const next = dayjs(weekStart).add(deltaWeeks, "week").toDate();
        setWeekByDate(next);
    }

    // ✅ DISPONIBILIDADE: só alterna livre/bloqueado (não abre modal)
    function handleAvailabilityClick(dayDate, slot) {
        if (!selectedTeacherId) return;

        // bloqueio por regra (horário fora do expediente): não mexe
        if (isOutsideBusinessHours(dayDate, slot)) return;

        const dayKey = dayjs(dayDate).format("YYYY-MM-DD");

        // se tem booking, não mexe — mas uma reserva de negociação vencida
        // é lixo e não deve travar a edição da disponibilidade
        const stored = fbBookings?.[dayKey]?.[slot] || null;
        if (stored && classifyBooking(stored, Date.now()) !== "expired") return;

        const dow = new Date(dayDate).getDay(); // 0..6

        const hasDateBlock = !!fbBlockedDates?.[dayKey]?.[slot];
        const hasWeeklyBlock = !!fbBlockedWeekly?.[dow]?.[slot];
        const hasOpenOverride = !!fbOpenOverrides?.[dayKey]?.[slot];

        setBlockCtx({
            dayDate, dayKey, dow, slot,
            hasDateBlock, hasWeeklyBlock,
            hasOpenOverride
        });
        setBlockDialogOpen(true);
    }


    function handleAppointmentsClick(dayDate, slot) {
        if (!selectedTeacherId) return;
        const dayKey = dayjs(dayDate).format("YYYY-MM-DD");

        // ❌ fora do expediente -> não abre
        if (isOutsideBusinessHours(dayDate, slot)) return;

        // ❌ bloqueado manual -> não abre
        const hasDateBlock = !!fbBlockedDates?.[dayKey]?.[slot];
        const dow = new Date(dayDate).getDay();
        const hasWeeklyBlock = !!fbBlockedWeekly?.[dow]?.[slot];
        const hasOpenOverride = !!fbOpenOverrides?.[dayKey]?.[slot];

        const blocked = hasDateBlock || (hasWeeklyBlock && !hasOpenOverride);
        if (blocked) return;

        if (hasDateBlock || hasWeeklyBlock) return;

        const stored = fbBookings?.[dayKey]?.[slot] || null;

        // Uma negociação viva é um horário que a Nalla já ofereceu a um lead e
        // está esperando resposta. Salvar por cima apaga a reserva sem que ele
        // saiba — então é preciso dizer isso em voz alta antes.
        if (stored && isNegotiationLock(stored) && !isLockExpired(stored, Date.now())) {
            const restante = lockRemainingLabel(stored, Date.now());
            const ok = window.confirm(
                `Este horário está em negociação com o lead ${stored.leadWaId || ""}` +
                `${restante ? ` (${restante})` : ""}.

` +
                "Agendar aqui cancela a reserva dele. Deseja continuar?"
            );
            if (!ok) return;
        }

        // Uma reserva (viva ou vencida) não é dado de agendamento: o formulário abre limpo.
        const existing = stored && !isNegotiationLock(stored) ? stored : null;
        setBookingCtx({ dayDate, slot, teacherId: selectedTeacherId, existing });
        setBookingOpen(true);
    }


    async function saveBooking(form) {
        if (!bookingCtx) return;

        const { dayDate, slot, teacherId } = bookingCtx;
        const dayKey = dayjs(dayDate).format("YYYY-MM-DD");

        try {
            await saveBookingFB(teacherId, dayKey, slot, {
                nome: form.nome?.trim(),
                escola: form.escola || "",
                bairro: form.bairro || "",
                idade: form.idade || "",
                conhecimento: form.conhecimento || "",
                compareceu: form.compareceu || "",
                matriculado: form.matriculado || "",
                color: form.color || "#213159",
            });

            setBookingOpen(false);
            setBookingCtx(null);
        } catch (e) {
            console.error(e);
            alert("Erro ao salvar agendamento no Firebase.");
        }
    }

    async function clearBooking() {
        if (!bookingCtx) return;

        const { dayDate, slot, teacherId } = bookingCtx;
        const dayKey = dayjs(dayDate).format("YYYY-MM-DD");

        try {
            await clearBookingFB(teacherId, dayKey, slot);

            setBookingOpen(false);
            setBookingCtx(null);
        } catch (e) {
            console.error(e);
            alert("Erro ao limpar horário no Firebase.");
        }
    }

    async function openOnlyThisDay() {
        if (!blockCtx || !selectedTeacherId) return;
        try {
            // força livre nesta data/slot (mesmo se weekly bloqueado)
            await setOpenOverride(selectedTeacherId, blockCtx.dayKey, blockCtx.slot, true);

            // se existir bloqueio pontual nesse slot/dia, remove (pra não contradizer)
            await setBlockedDate(selectedTeacherId, blockCtx.dayKey, blockCtx.slot, false);

            setBlockDialogOpen(false);
            setBlockCtx(null);
        } catch (e) {
            console.error(e);
            alert("Erro ao liberar somente este dia.");
        }
    }

    async function removeOpenOnlyThisDay() {
        if (!blockCtx || !selectedTeacherId) return;
        try {
            await setOpenOverride(selectedTeacherId, blockCtx.dayKey, blockCtx.slot, false);
            setBlockDialogOpen(false);
            setBlockCtx(null);
        } catch (e) {
            console.error(e);
            alert("Erro ao remover liberação deste dia.");
        }
    }

    // A reserva de negociação expira em 15 min. Sem um relógio, um horário
    // liberado continuaria aparecendo como ocupado até alguém recarregar a página.
    const [tick, setTick] = useState(() => Date.now());
    useEffect(() => {
        const id = setInterval(() => setTick(Date.now()), 30000);
        return () => clearInterval(id);
    }, []);

    const availability = useMemo(() => {
        if (!selectedTeacherId) return {};
        const nowTs = tick;

        const tId = selectedTeacherId;
        const map = { [tId]: {} };

        for (const d of weekDays) {
            const dayKey = dayjs(d).format("YYYY-MM-DD");
            map[tId][dayKey] = {};

            for (const slot of timeSlots) {
                // 1) booking — separando o agendamento definitivo da reserva que a
                // Nalla cria enquanto negocia. Reserva vencida não ocupa nada.
                const booking = fbBookings?.[dayKey]?.[slot];
                if (booking) {
                    const kind = classifyBooking(booking, nowTs);
                    if (kind !== "expired") {
                        map[tId][dayKey][slot] = { ...booking, type: kind === "negotiation" ? "negotiation" : "booking" };
                        continue;
                    }
                }

                // 2) regra de funcionamento
                const byRuleBlocked = isOutsideBusinessHours(d, slot);
                if (byRuleBlocked) {
                    map[tId][dayKey][slot] = { type: "blocked" };
                    continue;
                }

                // 3) override open (exceção) -> força LIVRE
                const openOverride = !!fbOpenOverrides?.[dayKey]?.[slot];
                if (openOverride) {
                    map[tId][dayKey][slot] = null;
                    continue;
                }

                // 4) bloqueios firebase
                const dow = new Date(d).getDay();
                const dateBlocked = !!fbBlockedDates?.[dayKey]?.[slot];
                const weeklyBlocked = !!fbBlockedWeekly?.[dow]?.[slot];

                const isBlocked = dateBlocked || weeklyBlocked;
                map[tId][dayKey][slot] = isBlocked ? { type: "blocked" } : null;
            }
        }

        return map;
        // `timeSlots` é constante de módulo: não entra nas dependências.
    }, [selectedTeacherId, weekDays, fbBlockedDates, fbBlockedWeekly, fbOpenOverrides, fbBookings, tick]);


    // KPIs do modo AGENDAMENTOS: conta bookings da semana (do professor selecionado)
    const bookingsThisWeek = useMemo(() => {
        if (!selectedTeacherId) return [];
        const out = [];
        for (const d of weekDays) {
            const dayKey = dayjs(d).format("YYYY-MM-DD");
            for (const slot of timeSlots) {
                const cell = availability?.[selectedTeacherId]?.[dayKey]?.[slot];
                if (cell?.type === "booking") { // negociação em curso não conta como agendamento
                    out.push({
                        dayKey,
                        day: d,
                        time: slot,
                        nome: cell.nome,
                        status: cell.matriculado === "Sim" ? "Concluído" : "Pendente",
                    });
                }
            }
        }
        return out;
    }, [availability, selectedTeacherId, weekDays]);

    const total = bookingsThisWeek.length;
    const confirmed = bookingsThisWeek.filter((b) => b.status === "Concluído").length;
    const pending = bookingsThisWeek.filter((b) => b.status !== "Concluído").length;

    async function blockDate() {
        if (!blockCtx || !selectedTeacherId) return;
        try {
            await setBlockedDate(selectedTeacherId, blockCtx.dayKey, blockCtx.slot, true);
            setBlockDialogOpen(false);
            setBlockCtx(null);
        } catch (e) {
            console.error(e);
            alert("Erro ao bloquear (pontual).");
        }
    }

    async function blockWeekly() {
        if (!blockCtx || !selectedTeacherId) return;
        try {
            await setBlockedWeekly(selectedTeacherId, blockCtx.dow, blockCtx.slot, true);
            setBlockDialogOpen(false);
            setBlockCtx(null);
        } catch (e) {
            console.error(e);
            alert("Erro ao bloquear (recorrente).");
        }
    }

    async function unblockDate() {
        if (!blockCtx || !selectedTeacherId) return;
        try {
            await setBlockedDate(selectedTeacherId, blockCtx.dayKey, blockCtx.slot, false);
            setBlockDialogOpen(false);
            setBlockCtx(null);
        } catch (e) {
            console.error(e);
            alert("Erro ao remover bloqueio pontual.");
        }
    }

    async function unblockWeekly() {
        if (!blockCtx || !selectedTeacherId) return;
        try {
            await setBlockedWeekly(selectedTeacherId, blockCtx.dow, blockCtx.slot, false);
            setBlockDialogOpen(false);
            setBlockCtx(null);
        } catch (e) {
            console.error(e);
            alert("Erro ao remover bloqueio recorrente.");
        }
    }

    return (
        <Box p={2}>
            {/* ===== Top Bento Header ===== */}
            <Paper
                elevation={0}
                sx={{
                    p: 2,
                    mb: 2,
                    borderRadius: 2,
                    border: "1px solid var(--border)",
                    bgcolor: "rgba(255,255,255,0.55)",
                    backdropFilter: "blur(12px)",
                }}
            >
                <Stack
                    direction={isMobile ? "column" : "row"}
                    justifyContent="space-between"
                    alignItems={isMobile ? "stretch" : "center"}
                    gap={1.5}
                >
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 950, color: "var(--cise-blue)" }}>
                            Agenda
                        </Typography>
                        <Typography sx={{ color: "var(--cise-text-muted)" }}>
                            {mode === "AGENDAMENTOS"
                                ? "Selecione um professor e clique em um horário LIVRE para cadastrar o lead"
                                : "Clique nos slots para alternar Livre/Bloqueado (sem modal)"}
                        </Typography>
                    </Box>

                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end" flexWrap="wrap">
                        <ModeSwitch value={mode} onChange={setMode} />

                        {/* Semana (igual nos dois modos) */}
                        <Stack direction="row" spacing={0.5} alignItems="center">
                            <Tooltip title="Semana anterior">
                                <IconButton size="small" onClick={() => shiftWeek(-1)} sx={{ border: "1px solid var(--border)" }}>
                                    <ChevronLeftRoundedIcon />
                                </IconButton>
                            </Tooltip>

                            <Tooltip title="Voltar para a semana atual">
                                <IconButton size="small" onClick={() => setWeekByDate(new Date())} sx={{ border: "1px solid var(--border)" }}>
                                    <TodayRoundedIcon />
                                </IconButton>
                            </Tooltip>

                            <Tooltip title="Próxima semana">
                                <IconButton size="small" onClick={() => shiftWeek(1)} sx={{ border: "1px solid var(--border)" }}>
                                    <ChevronRightRoundedIcon />
                                </IconButton>
                            </Tooltip>

                            <Chip
                                label={weekLabel}
                                size="small"
                                sx={{
                                    ml: 0.5,
                                    fontWeight: 950,
                                    border: "1px solid var(--border)",
                                    bgcolor: "rgba(255,255,255,0.70)",
                                }}
                            />
                        </Stack>

                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                value={dayjs(weekStart)}
                                onChange={handleWeekPick}
                                slotProps={{
                                    textField: {
                                        size: "small",
                                        sx: {
                                            minWidth: isMobile ? 150 : 190,
                                            "& .MuiOutlinedInput-root": {
                                                borderRadius: 999,
                                                background: "rgba(255,255,255,0.70)",
                                            },
                                        },
                                    },
                                }}
                            />
                        </LocalizationProvider>

                        {mode === "DISPONIBILIDADE" && (
                            <Button
                                size="small"
                                variant="contained"
                                startIcon={<BoltRoundedIcon />}
                                sx={{
                                    textTransform: "none",
                                    fontWeight: 950,
                                    bgcolor: "var(--cise-blue)",
                                    borderRadius: 999,
                                }}
                            >
                                Salvar
                            </Button>
                        )}
                    </Stack>
                </Stack>
            </Paper>

            {/* ===== KPIs (Agendamentos) ===== */}
            {mode === "AGENDAMENTOS" && (
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} md={4}>
                        <MiniMetric title="Total (Semana)" value={total} icon={<EventAvailableOutlinedIcon />} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <MiniMetric title="Concluídos" value={confirmed} icon={<CheckCircleOutlineOutlinedIcon />} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <MiniMetric title="Pendentes" value={pending} icon={<AccessTimeOutlinedIcon />} />
                    </Grid>
                </Grid>
            )}

            <Stack spacing={2}>
                {/* ===== Professores (horizontal) ===== */}
                <Paper
                    elevation={0}
                    sx={{
                        border: "1px solid var(--border)",
                        borderRadius: 2,
                        overflow: "hidden",
                        bgcolor: "rgba(255,255,255,0.55)",
                        backdropFilter: "blur(12px)",
                    }}
                >
                    <Box
                        sx={{
                            px: 2,
                            py: 1.5,
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            bgcolor: "rgba(33,49,89,0.04)",
                            borderBottom: "1px solid var(--border)",
                        }}
                    >
                        <Typography fontWeight={950}>Professores</Typography>

                        <Stack direction="row" spacing={1} alignItems="center">
                            <Typography sx={{ fontSize: 12, color: "var(--cise-text-muted)" }}>
                                Selecione um professor
                            </Typography>
                            <IconButton size="small" sx={{ border: "1px solid var(--border)" }}>
                                <MoreVertIcon fontSize="small" />
                            </IconButton>
                        </Stack>
                    </Box>

                    <Box
                        sx={{
                            p: 1.5,
                            display: "flex",
                            gap: 1,
                            overflowX: "auto",
                            WebkitOverflowScrolling: "touch",
                            "&::-webkit-scrollbar": { height: 8 },
                            "&::-webkit-scrollbar-thumb": { background: "rgba(33,49,89,0.20)", borderRadius: 999 },
                            "&::-webkit-scrollbar-track": { background: "transparent" },
                        }}
                    >
                        {teachers.map((t) => {
                            const active = t.id === selectedTeacherId;

                            return (
                                <Button
                                    key={t.id}
                                    onClick={() => setSelectedTeacherId(t.id)}
                                    variant="outlined"
                                    sx={{
                                        flex: "0 0 auto",
                                        minWidth: 220,
                                        justifyContent: "flex-start",
                                        gap: 1.2,
                                        textTransform: "none",
                                        fontWeight: 950,
                                        borderRadius: 2,
                                        border: "1px solid var(--border)",
                                        bgcolor: active ? "rgba(33,49,89,0.08)" : "rgba(255,255,255,0.70)",
                                        color: active ? "var(--cise-blue)" : "inherit",
                                        "&:hover": {
                                            bgcolor: active ? "rgba(33,49,89,0.10)" : "rgba(255,255,255,0.78)",
                                            border: "1px solid var(--border)",
                                        },
                                    }}
                                >
                                    <Avatar
                                        sx={{
                                            width: 40,
                                            height: 40,
                                            bgcolor: "rgba(33,49,89,0.12)",
                                            color: "var(--cise-blue)",
                                            fontWeight: 950,
                                        }}
                                    >
                                        {initials(t.name)}
                                    </Avatar>

                                    <Box sx={{ textAlign: "left", minWidth: 0, flex: 1 }}>
                                        <Typography fontWeight={950} noWrap>
                                            {t.name}
                                        </Typography>
                                        <Typography sx={{ fontSize: 12, color: "var(--cise-text-muted)" }} noWrap>
                                            {mode === "AGENDAMENTOS" ? "Ver agendamentos" : "Editar disponibilidade"}
                                        </Typography>
                                    </Box>

                                    <Chip
                                        label={active ? "Selecionado" : "—"}
                                        size="small"
                                        sx={{
                                            fontWeight: 950,
                                            border: "1px solid var(--border)",
                                            bgcolor: active ? "rgba(33,49,89,0.10)" : "transparent",
                                        }}
                                    />
                                </Button>
                            );
                        })}
                    </Box>
                </Paper>

                {/* ===== Calendar Panel ===== */}
                <Paper
                    elevation={0}
                    sx={{
                        border: "1px solid var(--border)",
                        borderRadius: 2,
                        overflow: "hidden",
                        bgcolor: "rgba(255,255,255,0.55)",
                        backdropFilter: "blur(12px)",
                    }}
                >
                    <Box
                        sx={{
                            px: 2,
                            py: 1.5,
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: 1,
                            bgcolor: "rgba(33,49,89,0.04)",
                            borderBottom: "1px solid var(--border)",
                        }}
                    >
                        <Stack spacing={0.2}>
                            <Typography fontWeight={950}>
                                {mode === "AGENDAMENTOS" ? (
                                    <>
                                        Agendamentos • <span style={{ color: "var(--cise-blue)" }}>{currentTeacherName}</span>
                                    </>
                                ) : (
                                    <>
                                        Disponibilidade • <span style={{ color: "var(--cise-blue)" }}>{currentTeacherName}</span>
                                    </>
                                )}
                            </Typography>
                            <Typography sx={{ fontSize: 12, color: "var(--cise-text-muted)" }}>
                                {mode === "AGENDAMENTOS"
                                    ? "Clique em um horário LIVRE para cadastrar o lead. Horários bloqueados não abrem."
                                    : "Clique para alternar Livre/Bloqueado (bookings ficam travados)."}
                            </Typography>
                        </Stack>
                    </Box>

                    {/* Mobile tabs */}
                    {isMobile && (
                        <Box sx={{ px: 1.5, pt: 1 }}>
                            <Tabs
                                value={mobileDayIdx}
                                onChange={(_, v) => setMobileDayIdx(v)}
                                variant="scrollable"
                                scrollButtons="auto"
                                sx={{ "& .MuiTab-root": { textTransform: "none", fontWeight: 950 } }}
                            >
                                {weekDays.map((d, idx) => (
                                    <Tab key={idx} label={fmtDayLabel(d)} />
                                ))}
                            </Tabs>
                            <Divider sx={{ mt: 1 }} />
                        </Box>
                    )}

                    {!isMobile ? (
                        <TeacherWeekGrid
                            weekDays={weekDays}
                            timeSlots={timeSlots}
                            availability={availability}
                            teacherId={selectedTeacherId}
                            mode={mode}
                            onCellClick={mode === "DISPONIBILIDADE" ? handleAvailabilityClick : handleAppointmentsClick}
                            height={720}
                        />
                    ) : (
                        <TeacherWeekGrid
                            weekDays={[weekDays[mobileDayIdx]]}
                            timeSlots={timeSlots}
                            availability={availability}
                            teacherId={selectedTeacherId}
                            mode={mode}
                            onCellClick={mode === "DISPONIBILIDADE" ? handleAvailabilityClick : handleAppointmentsClick}
                            height={620}
                        />
                    )}

                    <Box sx={{ px: 2, py: 1.5, borderTop: "1px solid var(--border)" }}>
                        <Typography sx={{ fontSize: 12, color: "var(--cise-text-muted)" }}>
                            💡 Booking criado no AGENDAMENTOS aparece automaticamente na DISPONIBILIDADE (nome do lead).
                        </Typography>
                    </Box>
                </Paper>
            </Stack>

            {/* Dialog apenas para AGENDAMENTOS */}
            <BookingDialog
                open={bookingOpen}
                onClose={() => {
                    setBookingOpen(false);
                    setBookingCtx(null);
                }}
                onSave={saveBooking}
                onClear={clearBooking}
                teacherName={currentTeacherName}
                teacherId={selectedTeacherId}
                dayDate={bookingCtx?.dayDate}
                slotTime={bookingCtx?.slot}
                initialData={bookingCtx?.existing?.type === "booking" ? bookingCtx.existing : null}
            />

            <BlockChoiceDialog
                open={blockDialogOpen}
                onClose={() => {
                    setBlockDialogOpen(false);
                    setBlockCtx(null);
                }}
                ctx={blockCtx}
                onBlockDate={blockDate}
                onBlockWeekly={blockWeekly}
                onUnblockDate={unblockDate}
                onUnblockWeekly={unblockWeekly}
                onOpenOnlyThisDay={openOnlyThisDay}
                onRemoveOpenOnlyThisDay={removeOpenOnlyThisDay}
            />
        </Box>
    );
}